import type {
  LongevityDayPackage,
  LongevityPlanDaysResponse,
} from '../../features/execute/api';
import type { LongevityPlan } from '../../features/execute/longevity';
import type {
  PersonalizedKnowledgeQueueResponse,
  PodcastSeries,
} from '../../features/knowledge/api';

const CACHE_TTL_MS = 5 * 60 * 1000;
const HUB_CACHE_KEY = 'aeterna_custom_course_hub';
const LESSON_CACHE_PREFIX = 'aeterna_custom_course_lesson:';
const PODCAST_SERIES_CACHE_KEY = 'aeterna_knowledge_podcast_series';

interface CacheEntry<T> {
  data: T;
  ts: number;
}

interface CacheOptions {
  allowStale?: boolean;
}

export interface CustomCourseHubSnapshot {
  plan: LongevityPlan | null;
  days: LongevityPlanDaysResponse['days'];
  queue?: PersonalizedKnowledgeQueueResponse | null;
}

let hubCache: CacheEntry<CustomCourseHubSnapshot> | null = null;
let podcastSeriesCache: CacheEntry<PodcastSeries[]> | null = null;
const lessonCache = new Map<string, CacheEntry<LongevityDayPackage>>();

function isFresh<T>(entry: CacheEntry<T> | null | undefined): entry is CacheEntry<T> {
  if (!entry) return false;
  return Date.now() - entry.ts < CACHE_TTL_MS;
}

function readSessionEntry<T>(key: string): CacheEntry<T> | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as CacheEntry<T>;
  } catch {
    window.sessionStorage.removeItem(key);
    return null;
  }
}

function writeSessionEntry<T>(key: string, entry: CacheEntry<T>) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Storage quota or privacy mode should not block the page.
  }
}

function resolveSessionBackedEntry<T>(
  current: CacheEntry<T> | null,
  key: string,
): CacheEntry<T> | null {
  if (!current || !isFresh(current)) {
    const sessionEntry = readSessionEntry<T>(key);
    if (sessionEntry) return sessionEntry;
  }
  return current;
}

function getCacheData<T>(entry: CacheEntry<T> | null, options: CacheOptions = {}) {
  if (!entry) return null;
  if (!options.allowStale && !isFresh(entry)) return null;
  return entry.data;
}

function lessonKey(planId: string, day: number) {
  return `${planId}:${day}`;
}

export function getCachedCustomCourseHub(options: CacheOptions = {}): CustomCourseHubSnapshot | null {
  hubCache = resolveSessionBackedEntry(hubCache, HUB_CACHE_KEY);
  return getCacheData(hubCache, options);
}

export function setCachedCustomCourseHub(snapshot: CustomCourseHubSnapshot) {
  hubCache = { data: snapshot, ts: Date.now() };
  writeSessionEntry(HUB_CACHE_KEY, hubCache);
}

export function getCachedCustomCourseLesson(planId: string, day: number): LongevityDayPackage | null {
  const key = lessonKey(planId, day);
  let cached = lessonCache.get(key);
  if (!isFresh(cached)) {
    cached = readSessionEntry<LongevityDayPackage>(`${LESSON_CACHE_PREFIX}${key}`) ?? undefined;
    if (cached) lessonCache.set(key, cached);
  }
  if (!isFresh(cached)) return null;
  return cached.data;
}

export function setCachedCustomCourseLesson(planId: string, day: number, lesson: LongevityDayPackage) {
  const key = lessonKey(planId, day);
  const entry = { data: lesson, ts: Date.now() };
  lessonCache.set(key, entry);
  writeSessionEntry(`${LESSON_CACHE_PREFIX}${key}`, entry);
}

export function getCachedPodcastSeries(options: CacheOptions = {}): PodcastSeries[] | null {
  podcastSeriesCache = resolveSessionBackedEntry(podcastSeriesCache, PODCAST_SERIES_CACHE_KEY);
  return getCacheData(podcastSeriesCache, options);
}

export function setCachedPodcastSeries(series: PodcastSeries[]) {
  podcastSeriesCache = { data: series, ts: Date.now() };
  writeSessionEntry(PODCAST_SERIES_CACHE_KEY, podcastSeriesCache);
}
